import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationService } from '../api/services';
import '../pages/Bounty.css';

// 已顯示過的通知 ID（Session 層級，避免重整後重複跳出）；以 sessionStorage 為基礎，頁面關閉即清除
const SHOWN_KEY = 'tech-forum:bounty-toast-shown';

const getShown = () => {
  try { return new Set(JSON.parse(sessionStorage.getItem(SHOWN_KEY) || '[]')); } catch { return new Set(); }
};
const addShown = (id) => {
  try {
    const s = getShown();
    s.add(id);
    sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...s]));
  } catch { /* 略過 */ }
};

// 廣播 toast：當開立者在線上時，顯示「xxx案件 : 誰誰誰提出結案」彈跳卡片（左下角）
const BountySubmitToast = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const shownRef = useRef(getShown());
  const intervalRef = useRef(null);

  // 輪詢通知列表，過濾出尚未顯示的 BOUNTY_SUBMITTED 未讀通知
  const poll = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await notificationService.list();
      const items = Array.isArray(res.data) ? res.data : [];
      const fresh = items.filter(
        (n) => (n.type === 'BOUNTY_SUBMITTED' || n.type === 'BOUNTY_CLAIMED' || n.type === 'BADGE_AWARDED') && !n.is_read && !shownRef.current.has(n.id)
      );
      if (!fresh.length) return;
      // 同一懸賞只保留一張卡片，避免反覆送出結案累積多筆未讀通知時重複跳出
      const seenBounty = new Set();
      const deduped = [];
      fresh.forEach((n) => {
        shownRef.current.add(n.id);
        addShown(n.id);
        if (n.bounty_id && seenBounty.has(n.bounty_id)) return;
        if (n.bounty_id) seenBounty.add(n.bounty_id);
        deduped.push(n);
      });
      if (!deduped.length) return;
      setToasts((prev) => [...prev, ...deduped]);
    } catch { /* 靜默失敗 */ }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    poll();
    // 15 秒輪詢一次，讓開立者在線上時近乎即時看到結案彈跳卡片（不需手動重整）
    intervalRef.current = setInterval(poll, 15000);
    return () => clearInterval(intervalRef.current);
  }, [isAuthenticated, poll]);

  // 自動消除：每張卡片顯示 9 秒後自動淡出
  useEffect(() => {
    if (!toasts.length) return undefined;
    const timer = setTimeout(() => {
      setToasts((prev) => prev.slice(1));
    }, 9000);
    return () => clearTimeout(timer);
  }, [toasts]);

  const dismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const handleClick = async (n) => {
    dismiss(n.id);
    // 點擊即視為已讀：先 await 確保寫入後端再導頁，避免後續輪詢或重整時重複跳出（忽略失敗，不阻斷導頁）
    try { await notificationService.markRead(n.id); } catch { /* 略過 */ }
    if (n.bounty_id) navigate(`/bounties/${n.bounty_id}`);
  };

  if (!toasts.length) return null;

  // 依通知類型決定彈跳卡片的圖示、標籤、標題與內文：領取、結案、獲得榮譽徽章三種事件
  const cardMeta = (n) => {
    const actor = n.actor_name || '有人';
    if (n.type === 'BOUNTY_CLAIMED') {
      return { icon: '🙋', label: '有人領取', sub: `${actor} 領取了你的懸賞` };
    }
    if (n.type === 'BADGE_AWARDED') {
      // 榮譽徽章名稱存於通知的 content 欄位
      return { icon: '🏅', label: '獲得徽章', title: n.content || '榮譽徽章', sub: `${actor} 頒發給你` };
    }
    return { icon: '📋', label: '結案申請', sub: `${actor} 提出結案，請確認` };
  };

  return (
    <div className="bounty-toast-container">
      {toasts.map((n) => {
        const meta = cardMeta(n);
        return (
        <div key={n.id} className="bounty-toast-card" onClick={() => handleClick(n)} role="button" tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleClick(n)}>
          <span className="bounty-toast-icon">{meta.icon}</span>
          <div className="bounty-toast-body">
            <div className="bounty-toast-label">{meta.label}</div>
            <div className="bounty-toast-title">{meta.title || n.bounty_title || '懸賞案件'}</div>
            <div className="bounty-toast-sub">{meta.sub}</div>
          </div>
          <button
            type="button"
            className="bounty-toast-close"
            onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
            aria-label="關閉"
          >✕</button>
        </div>
        );
      })}
    </div>
  );
};

export default BountySubmitToast;
